
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const formSchema = z.object({
  webhookUrl: z.string().url("Please enter a valid webhook URL"),
  channelName: z.string().min(1, "Channel name is required"),
  isEnabled: z.boolean()
});

type FormData = z.infer<typeof formSchema>;

interface SlackConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export const SlackConfigDialog = ({
  open,
  onOpenChange,
  userId
}: SlackConfigDialogProps) => {
  const [isTesting, setIsTesting] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: config
  } = useQuery({
    queryKey: ['slack-config', userId],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('slack_configs').select('*').eq('user_id', userId).single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!userId && open // Only fetch when dialog is open
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      webhookUrl: '',
      channelName: '',
      isEnabled: false
    }
  });

  // Update form values when config data is loaded
  useEffect(() => {
    if (config) {
      form.reset({
        webhookUrl: config.webhook_url || '',
        channelName: config.channel_name || '',
        isEnabled: config.is_enabled || false
      });
    }
  }, [config, form]);

  const testConnection = async (webhookUrl: string) => {
    if (!webhookUrl) {
      toast.error("Please enter a webhook URL first");
      return;
    }
    
    setIsTesting(true);
    try {
      // Use our edge function to proxy the request to avoid CORS issues
      const { error } = await supabase.functions.invoke('send-slack-notification', {
        body: {
          test_mode: true,
          webhook_url: webhookUrl
        }
      });
      
      if (error) throw error;
      toast.success("Test message sent successfully!");
    } catch (error) {
      console.error('Test connection error:', error);
      toast.error("Failed to send test message. Please check your webhook URL.");
    } finally {
      setIsTesting(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // First check if a configuration already exists for this user
      const { data: existingConfig } = await supabase
        .from('slack_configs')
        .select('id')
        .eq('user_id', userId)
        .single();
      
      let result;
      
      if (existingConfig) {
        // Update existing config
        result = await supabase
          .from('slack_configs')
          .update({
            webhook_url: data.webhookUrl,
            channel_name: data.channelName,
            is_enabled: data.isEnabled
          })
          .eq('id', existingConfig.id);
      } else {
        // Insert new config
        result = await supabase
          .from('slack_configs')
          .insert({
            user_id: userId,
            webhook_url: data.webhookUrl,
            channel_name: data.channelName,
            is_enabled: data.isEnabled
          });
      }
      
      if (result.error) throw result.error;
      
      toast.success("Slack configuration saved successfully!");
      queryClient.invalidateQueries({
        queryKey: ['slack-config', userId]
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error("Failed to save Slack configuration");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect Slack</DialogTitle>
          <DialogDescription>
            Configure your Slack integration to receive notifications about new leads and important updates.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h3 className="font-medium text-base">Enable Slack Notifications</h3>
                  <p className="text-sm text-muted-foreground">Test connection to enable</p>
                </div>
                <FormField
                  control={form.control}
                  name="isEnabled"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="webhookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Webhook URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://hooks.slack.com/services/..." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="channelName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Channel Name</FormLabel>
                  <FormControl>
                    <Input placeholder="#leads" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => testConnection(form.getValues().webhookUrl)}
                disabled={isTesting || !form.getValues().webhookUrl}
              >
                {isTesting ? "Testing..." : "Test Connection"}
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90">Save Changes</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
